import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { RTL_LOCALES, type Locale } from '@easymoving/types';
import he from './locales/he.json';
import en from './locales/en.json';
import ru from './locales/ru.json';
import fr from './locales/fr.json';
import ar from './locales/ar.json';

export const SUPPORTED_LOCALES: Locale[] = ['he', 'en', 'ru', 'fr', 'ar'];
export const LOCALE_STORAGE_KEY = 'easymoving.locale';

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Hebrew-first market, but the audience is multilingual (he/en/ru/fr move
    // in Israel). So: respect an explicit prior choice (localStorage), else
    // sniff the browser language, else fall back to Hebrew (handoff §5).
    fallbackLng: 'he',
    supportedLngs: SUPPORTED_LOCALES,
    load: 'languageOnly', // map en-US → en, ru-RU → ru, etc.
    nonExplicitSupportedLngs: true,
    resources: {
      he: { common: he },
      en: { common: en },
      ru: { common: ru },
      fr: { common: fr },
      ar: { common: ar },
    },
    defaultNS: 'common',
    ns: ['common'],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    returnNull: false,
  });

/** Set <html dir>/<html lang> — RTL only for Hebrew. */
function applyDirection(lng: string) {
  const base = (lng.split('-')[0] ?? 'he') as Locale;
  const dir = RTL_LOCALES.has(base) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', base);
}

applyDirection(i18n.resolvedLanguage ?? i18n.language ?? 'he');
i18n.on('languageChanged', applyDirection);

export default i18n;
